import type { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@opswat/ui';
import { Link } from 'components/Link';

import { FOOTER_LEGAL_LINKS, FOOTER_SOCIAL_LINKS } from './Footer.data';

export type FooterIconName = ComponentProps<typeof Icon>['iconName'];

export type FooterSocialLink = {
    labelKey: string;
    href: string;
} & ({ iconName: FooterIconName; imgSrc?: never } | { iconName?: never; imgSrc: string });

export type FooterLegalLink = { labelKey: string; href: string };

export const Footer = () => {
    const { t } = useTranslation();
    const currentYear = new Date(Date.now()).getFullYear();

    return (
        <footer className='tw:w-full tw:bg-neutrals-200 tw:dark:bg-neutrals-1100'>
            <div className='tw:mx-auto tw:px-20 tw:max-w-1300'>
                <div className='tw:flex tw:items-center tw:justify-between tw:gap-20 tw:h-96 tw:border-b tw:border-neutrals-300 tw:dark:border-neutrals-900'>
                    <Link
                        to='/'
                        className='tw:no-underline hover:tw:no-underline focus-visible:tw:outline focus-visible:tw:outline-1 focus-visible:tw:outline-neutrals-700 tw:dark:focus-visible:tw:outline-white tw:rounded-sm'
                    >
                        <div className='tw:flex tw:flex-col tw:gap-2 tw:text-neutrals-1200 tw:dark:text-white'>
                            <div className='tw:text-footnote tw:font-medium tw:uppercase tw:tracking-wider'>
                                {t('METADEFENDER')}
                            </div>
                            <div className='tw:text-headline-m tw:font-medium'>{t('Cloud')}</div>
                        </div>
                    </Link>
                    <div className='tw:flex tw:items-center tw:gap-20'>
                        {FOOTER_SOCIAL_LINKS.map((link) => (
                            <Link
                                key={link.href}
                                to={link.href}
                                isExternal
                                aria-label={t(link.labelKey)}
                                className='tw:relative tw:inline-flex tw:items-center tw:justify-center tw:text-neutrals-1200 tw:dark:text-white hover:tw:text-neutrals-1200 tw:dark:hover:text-white tw:rounded-full focus-visible:tw:outline focus-visible:tw:outline-1 focus-visible:tw:outline-neutrals-700 tw:dark:focus-visible:tw:outline-white'
                            >
                                {link.iconName ? (
                                    <Icon iconName={link.iconName} className='tw:size-20' />
                                ) : (
                                    <img
                                        src={link.imgSrc}
                                        alt=''
                                        aria-hidden='true'
                                        className='tw:size-20 tw:dark:invert'
                                    />
                                )}
                                <span className='tw:sr-only'>{t(link.labelKey)}</span>
                            </Link>
                        ))}
                    </div>
                </div>
                <div className='tw:py-20 tw:grid tw:gap-20 tw:sm:grid-cols-2 tw:lg:grid-cols-4'>
                    {FOOTER_LEGAL_LINKS.map((link) => (
                        <div key={link.href} className='tw:flex tw:flex-col tw:gap-4'>
                            <span className='tw:text-note tw:font-medium tw:text-neutrals-1200 tw:dark:text-white'>
                                {t(link.labelKey)}
                            </span>
                            <Link
                                to={link.href}
                                isExternal
                                isNote
                                isBrand
                                className='tw:break-all tw:visited:text-blue-700 tw:dark:visited:text-blue-400'
                            >
                                {link.href}
                            </Link>
                        </div>
                    ))}
                    <span className='tw:self-end tw:text-right tw:text-note tw:text-neutrals-600 tw:dark:text-neutrals-500'>
                        {t('©{{year}} OPSWAT, Inc. All rights reserved.', { year: currentYear })}
                    </span>
                </div>
            </div>
        </footer>
    );
};
